import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import CancelIcon as CancelNewIcon from "@mui/icons-material/Cancel";
import moment from "moment";
import { toast } from "react-toastify";
import { getAllEmirates } from "../../../api/emirates";
import { getAllRoutes } from "../../../api/routes";
import { getAllBustop } from "../../../api/bustop";
import { updateApplciation } from "../../../api/application";

const StudentDetailsDialog = ({ open, onClose, student, onSave }) => {
  console.log("🚀 ~ StudentDetailsDialog ~ student:", student);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStudent, setEditedStudent] = useState({});
  console.log("🚀 ~ StudentDetailsDialog ~ editedStudent:", editedStudent);

  const [emirates, setEmirates] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [bustops, setBustops] = useState([]);

  // Filtered routes and bustops based on selected emirates
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredBustops, setFilteredBustops] = useState([]);

  const fieldsConfig = [
    { key: "Status", label: "Status" },
    ...(student?.rejectReason || student?.Status?.toLowerCase() === "rejected"
      ? [{ key: "rejectReason", label: "Rejection Reason" }]
      : []),
    { key: "Session", label: "Academic Session" },
    { key: "RollNo", label: "Roll Number" },
    { key: "classname", label: "Class" },
    { key: "section", label: "Section" },
    { key: "MemSName", label: "Student Name" },
    ...(student?.ServiceType !== "3"
      ? [
          { key: "EmiratesId", label: "Emirates" },
          { key: "RouteId", label: "Route" },
        ]
      : []),
    ...(student?.ServiceType === "1" ? [] : [{ key: "BusStopId", label: "Bus Stop" }]),
    ...(student?.ServiceType !== "1"
      ? [
          { key: "oldEmiratesid", label: "Old Emirates" },
          { key: "oldRouteid", label: "Old Route" },
          { key: "oldBustopid", label: "Old Bus Stop" },
        ]
      : []),
    ...(student?.ServiceType !== "3"
      ? [{ key: "DateofAdmission", label: "Confirmation Date" }]
      : []),
    { key: "addeddate", label: "Added Date" },
    { key: "EffectiveStartDate", label: "Effective Start Date" },
    ...(student?.ServiceType !== "2" && student?.ServiceType !== "3"
      ? [
          { key: "NoticeDate", label: "Notice Date" },
          { key: "CancellationDate", label: "Cancellation Date" },
          { key: "CancellationReason", label: "Cancellation Reason" },
        ]
      : []),
  ];
  const handleClose = () => {
    onClose();
    setIsEditing(false);
  };

  const fetchEmirates = async () => {
    try {
      const data = await getAllEmirates();
      setEmirates(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const data = await getAllRoutes();
      setRoutes(data.data);
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  const fetchBustops = async () => {
    try {
      const data = await getAllBustop();
      setBustops(data.data);
    } catch (error) {
      console.error("Error fetching bustops:", error);
    }
  };

  useEffect(() => {
    fetchEmirates();
    fetchRoutes();
    fetchBustops();
  }, []);

  // Reset editedStudent when student prop changes
  useEffect(() => {
    if (student) {
      // Combine student and StudentDetails for editing
      const combinedStudent = {
        ...student,
        ...student.StudentDetails,
        Session: student.schoolAcademicYear || student.Session || student.StudentDetails?.Session || "",
        classname: student.grade || student.classname || student.StudentDetails?.classname || "",
        section: student.division || student.section || student.StudentDetails?.section || "",
        MemSName:
          student.studentName ||
          student.StudentName ||
          student.MemFName ||
          student.MemSName ||
          student.StudentDetails?.MemFName ||
          student.StudentDetails?.MemSName ||
          "",
      };

      setEditedStudent(combinedStudent);

      // Filter routes when component mounts
      const emiratesRoutes = Array.isArray(routes)
        ? routes.filter(
            (route) =>
              route?.Area?.Emirate?.eid === parseInt(student.EmiratesId || student.emirateid) ||
              route?.emirateid === parseInt(student.EmiratesId || student.emirateid) ||
              route?.eid === parseInt(student.EmiratesId || student.emirateid)
          )
        : [];
      setFilteredRoutes(emiratesRoutes);

      // Filter bustops based on selected route
      const routeBustops = Array.isArray(bustops)
        ? bustops.filter(
            (bustop) =>
              bustop?.Route?.rid === parseInt(student.RouteId || student.routeid) ||
              bustop?.routeid === parseInt(student.RouteId || student.routeid) ||
              bustop?.rid === parseInt(student.RouteId || student.routeid)
          )
        : [];
      setFilteredBustops(routeBustops);
    }
  }, [student, routes, bustops]);

  // Handle Emirates selection and subsequent route filtering
  const handleEmiratesChange = (e) => {
    const selectedEmiratesId = e.target.value;

    // Update editedStudent
    setEditedStudent((prev) => ({
      ...prev,
      EmiratesId: selectedEmiratesId,
      RouteId: "", // Reset route when emirates changes
      BusStopId: "", // Reset bustop when emirates changes
    }));

    // Filter routes for selected emirates
    const emiratesRoutes = Array.isArray(routes)
      ? routes.filter(
          (route) =>
            route?.Area?.Emirate?.eid === parseInt(selectedEmiratesId) ||
            route?.emirateid === parseInt(selectedEmiratesId) ||
            route?.eid === parseInt(selectedEmiratesId)
        )
      : [];
    setFilteredRoutes(emiratesRoutes);
    setFilteredBustops([]); // Clear bustops
  };

  // Handle Route selection and subsequent bustop filtering
  const handleRouteChange = (e) => {
    const selectedRouteId = e.target.value;

    // Update editedStudent
    setEditedStudent((prev) => ({
      ...prev,
      RouteId: selectedRouteId,
      BusStopId: "", // Reset bustop when route changes
    }));

    // Filter bustops for selected route
    const routeBustops = Array.isArray(bustops)
      ? bustops.filter(
          (bustop) =>
            bustop?.Route?.rid === parseInt(selectedRouteId) ||
            bustop?.routeid === parseInt(selectedRouteId) ||
            bustop?.rid === parseInt(selectedRouteId)
        )
      : [];
    setFilteredBustops(routeBustops);
  };

  // Handle Bustop selection
  const handleBustopChange = (e) => {
    const selectedBustopId = e.target.value;

    // Update editedStudent
    setEditedStudent((prev) => ({
      ...prev,
      BusStopId: selectedBustopId,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "Session" || name === "classname") {
      newValue = value.replace(/[^0-9]/g, "");
    }
    setEditedStudent((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSave = async () => {
    console.log(editedStudent);
    // Prepare the request body
    const formBody = {
      id: editedStudent.id,
      RollNo: editedStudent.RollNo,
      Status: editedStudent.Status,
      EmiratesId: editedStudent.EmiratesId,
      RouteId: editedStudent.RouteId,
      BusStopId: editedStudent.BusStopId,
      NoticeDate: editedStudent.NoticeDate,
      addeddate: editedStudent.addeddate,
      classname: editedStudent.classname,
      section: editedStudent.section,
      DateofAdmission: editedStudent.DateofAdmission,
      Location: editedStudent.Location,
      oldLocation: editedStudent.oldLocation,
      oldEmiratesid: editedStudent.oldEmiratesid,
      oldRouteid: editedStudent.oldRouteid,
      oldBustopid: editedStudent.oldBustopid,
      CancellationDate: editedStudent.CancellationDate,
      CancellationReason: editedStudent.CancellationReason,
      EffectiveStartDate: editedStudent.EffectiveStartDate,
    };

    try {
      // Use the imported updateStudent function
      const result = await updateApplciation(editedStudent.id, formBody);

      // Optional: Additional actions after successful update
      setIsEditing(false);
      onSave(result.data);
      // onClose();
    } catch (error) {
      console.error("Error updating student:", error);
      // Error handling is now managed by the toast.promise in the API function
    }
  };

  const handleApproveReject = async (status) => {
    if (status === "approved" && student.Status === "approved") {
      return toast.error("Already Approved");
    }
    if (status === "rejected" && student.Status === "rejected") {
      return toast.error("Already Rejected");
    }

    const formBody = {
      id: editedStudent.id,
      RollNo: editedStudent.RollNo,
      Status: status,
    };

    try {
      // Use the imported updateStudent function
      const result = await updateApplciation(editedStudent.id, formBody);
      onSave(result.data);
      // onClose();
    } catch (error) {
      console.error("Error updating student:", error);
    }
    console.log("🚀 ~ handleApprove ~ fromBody:", formBody);
  };

  const handleCancel = () => {
    // setEditedStudent(student);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    // Check if dateString is a valid date
    if (!dateString) return "N/A";

    // Try parsing ISO date string or timestamp
    const parsedDate = moment(dateString);

    // Check if the parsed date is valid
    return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "N/A";
  };

  // If no student is selected, show loading or placeholder
  if (!student) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading student details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Dropdown-specific fields
  const dropdownFields = [
    {
      key: "EmiratesId",
      label: "Emirates",
      options: emirates,
      valueKey: "eid",
      displayKey: "name",
      onChange: (e) => {
        const selectedEmiratesId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          EmiratesId: selectedEmiratesId,
          RouteId: "", // Reset route when emirates changes
          BusStopId: "", // Reset bustop when emirates changes
        }));

        const emiratesRoutes = routes.data.filter(
          (route) => route.Area.Emirate.eid === parseInt(selectedEmiratesId)
        );
        setFilteredRoutes(emiratesRoutes);
        setFilteredBustops([]); // Clear bustops
      },
    },
    {
      key: "RouteId",
      label: "Route",
      options: filteredRoutes,
      valueKey: "rid",
      displayKey: "routename",
      onChange: (e) => {
        const selectedRouteId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          RouteId: selectedRouteId,
          BusStopId: "", // Reset bustop when route changes
        }));

        const routeBustops = bustops.data.filter(
          (bustop) => bustop.Route.rid === parseInt(selectedRouteId)
        );
        setFilteredBustops(routeBustops);
      },
    },
    {
      key: "BusStopId",
      label: "Bus Stop",
      options: filteredBustops,
      valueKey: "sid",
      displayKey: "bustopname",
      onChange: (e) => {
        const selectedBustopId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          BusStopId: selectedBustopId,
        }));
      },
    },
    {
      key: "oldEmiratesid",
      label: "Old Emirates",
      options: emirates,
      valueKey: "eid",
      displayKey: "name",
      onChange: (e) => {
        const selectedEmiratesId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          oldEmiratesid: selectedEmiratesId,
          oldRouteid: "", // Reset old route when old emirates changes
          oldBustopid: "", // Reset old bustop when old emirates changes
        }));

        const emiratesRoutes = routes.data.filter(
          (route) => route.Area.Emirate.eid === parseInt(selectedEmiratesId)
        );
        setFilteredRoutes(emiratesRoutes);
        setFilteredBustops([]); // Clear bustops
      },
    },
    {
      key: "oldRouteid",
      label: "Old Route",
      options: filteredRoutes,
      valueKey: "rid",
      displayKey: "routename",
      onChange: (e) => {
        const selectedRouteId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          oldRouteid: selectedRouteId,
          oldBustopid: "", // Reset old bustop when old route changes
        }));

        const routeBustops = bustops.data.filter(
          (bustop) => bustop.Route.rid === parseInt(selectedRouteId)
        );
        setFilteredBustops(routeBustops);
      },
    },
    {
      key: "oldBustopid",
      label: "Old Bus Stop",
      options: filteredBustops,
      valueKey: "sid",
      displayKey: "bustopname",
      onChange: (e) => {
        const selectedBustopId = e.target.value;

        setEditedStudent((prev) => ({
          ...prev,
          oldBustopid: selectedBustopId,
        }));
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ "& .MuiDialog-paper": { m: { xs: 1, sm: 2 }, width: { xs: "calc(100% - 16px)", sm: "100%" } } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "primary.main",
          color: "white",
          px: { xs: 2, sm: 3 },
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>Student Details</Typography>
        <IconButton onClick={handleClose} color="inherit" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 1.5, sm: 3 },
            backgroundColor: "background.default",
          }}
        >
          {/* Render other fields */}
          {fieldsConfig
            .filter((field) => !["EmiratesName", "RouteName", "BustopName"].includes(field.key))
            .map((field) => (
              <Box
                key={field.key}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  marginBottom: 2,
                  paddingBottom: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                {isEditing ? (
                  // Dropdown selection for Emirates, Route, and Bus Stop during edit
                  dropdownFields.find((df) => df.key === field.key) ? (
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        name={field.key}
                        value={editedStudent[field.key] || ""}
                        label={field.label}
                        onChange={dropdownFields.find((df) => df.key === field.key).onChange}
                      >
                        {dropdownFields
                          .find((df) => df.key === field.key)
                          .options.map((option) => (
                            <MenuItem
                              key={
                                option[dropdownFields.find((df) => df.key === field.key).valueKey]
                              }
                              value={
                                option[dropdownFields.find((df) => df.key === field.key).valueKey]
                              }
                            >
                              {option[dropdownFields.find((df) => df.key === field.key).displayKey]}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  ) : (
                    // Existing TextField for other fields
                    <TextField
                      fullWidth
                      name={field.key}
                      label={field.label}
                      value={
                        ["DateofAdmission", "addeddate", "NoticeDate", "CancellationDate", "EffectiveStartDate"].includes(
                          field.key
                        )
                          ? formatDate(editedStudent[field.key])
                          : editedStudent[field.key] || ""
                      }
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      type={
                        ["DateofAdmission", "addeddate", "NoticeDate", "CancellationDate", "EffectiveStartDate"].includes(
                          field.key
                        )
                          ? "date"
                          : "text"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{ flex: 1 }}
                      InputProps={{
                        readOnly: field.key === "Status",
                      }}
                    />
                  )
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ flex: 1, color: "text.secondary", fontWeight: 600 }}>
                      {field.label}
                    </Typography>
                    <Typography variant="body1" sx={{ flex: 1, textAlign: { xs: "left", sm: "right" }, wordBreak: "break-word" }}>
                      {field.key === "rejectReason"
                        ? student.rejectReason || "N/A"
                        : field.key === "Session"
                        ? student.StudentDetails?.Session
                        : field.key === "classname"
                        ? student.StudentDetails?.classname
                        : field.key === "section"
                        ? student.StudentDetails?.section
                        : field.key === "MemSName"
                        ? student.StudentDetails?.MemSName
                        : field.key === "EmiratesId"
                        ? student.EmiratesName
                        : field.key === "RouteId"
                        ? student.RouteName
                        : field.key === "BusStopId"
                        ? student.BustopName
                        : field.key === "oldEmiratesid"
                        ? student.oldEmirates
                        : field.key === "oldRouteid"
                        ? student.oldRoute
                        : field.key === "oldBustopid"
                        ? student.oldBustop
                        : [
                            "DateofAdmission",
                            "addeddate",
                            "NoticeDate",
                            "CancellationDate",
                            "EffectiveStartDate",
                          ].includes(field.key)
                        ? formatDate(student[field.key])
                        : student[field.key] || "N/A"}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: 2,
        }}
      >
        <Box>
          {isEditing ? (
            <>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                variant="contained"
                onClick={handleSave}
                sx={{ marginRight: 1 }}
              >
                Save
              </Button>
              <Button
                startIcon={<CancelIcon />}
                color="primary"
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                startIcon={<CloseIcon />}
                color="primary"
                variant="contained"
                onClick={onClose}
                sx={{ marginRight: 1 }}
              >
                Close
              </Button>
              <Button
                startIcon={<EditIcon />}
                color="primary"
                variant="contained"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StudentDetailsDialog;
